/* ============================================
   STADIUM AUDIO ENGINE
   ============================================
   Moteur audio multi-couches avec Web Audio API.

   Couche 1 — Ambiance de fond (loop crossfade)
   Couche 2 — État de jeu (overlay conditionnel, machine d'états)
   Couche 3 — Événements ponctuels (one-shots & sound stacks)
   Couche 4 — Chants de fond (one-shots aléatoires, bas volume)

   Bus master :
     [Layer 1 Gain] ─┐
     [Layer 2 Gain] ─┤
     [Layer 3 Gain] ─┤─▶ Master Gain ─▶ Compressor ─▶ Limiter ─▶ Output
     [Layer 4 Gain] ─┘

   Gouvernance :
     EventResolver — arbitrage play/ignore/replace/transition
     DuckManager   — duck centralisé de Layer 1
   ============================================ */

import { SOUND_FILES, LAYER2_STATES, SOUND_FALLBACKS, LAYER4_CONFIG } from './sound-mapping.js'
import { EventResolver } from './event-resolver.js'
import { DuckManager } from './duck-manager.js'

/** Compteur auto-incrémenté pour identifier chaque source (duck, cleanup). */
let _sourceIdCounter = 0

class StadiumAudioEngine {
  constructor() {
    this.ctx = null
    this.enabled = false
    this.initialized = false
    this.matchActive = false

    // Audio graph nodes
    this.masterGain = null
    this.compressor = null
    this.limiter = null
    this.layer1Gain = null
    this.layer2Gain = null
    this.layer3Gain = null
    this.layer4Gain = null

    // Pre-loaded audio buffers  { key: AudioBuffer }
    this.buffers = {}

    // Layer 1 — Ambiance (crossfade loop)
    this.layer1 = {
      currentSource: null,
      currentSourceGain: null,
      crossfadeTimer: null,
      isPlaying: false
    }

    // Layer 2 — Game state overlay (machine d'états)
    this.layer2 = {
      currentSource: null,
      currentSourceGain: null,
      crossfadeTimer: null,
      isPlaying: false,
      currentState: 'idle'
    }

    // Layer 3 — One-shot events
    this.layer3 = {
      activeSources: new Set()
    }

    // Layer 4 — Chants de fond
    this.layer4 = {
      currentSource: null,
      currentSourceGain: null,
      timer: null,
      isPlaying: false,
      lastChantKey: null,  // éviter de répéter le même chant
      active: false        // true quand le match est en cours et les chants autorisés
    }

    // Composants
    this.resolver = new EventResolver()
    this.duckManager = null // initialisé dans init()

    // Dernière équipe reçue (pour logique domicile/extérieur future)
    this.lastTeam = null

    // Configurable parameters (overridable via SET_AUDIO_CONFIG)
    this.config = {
      masterVolume: 0.8,
      layer1Volume: 0.5,
      layer2Volume: 0.6,
      layer3Volume: 0.85,
      layer4Volume: LAYER4_CONFIG.volume,
      crossfadeDuration: 4,
      crossfadePreload: 5,
      layer3FadeIn: 0.05,
      layer3FadeOut: 0.3
    }
  }

  /* ──────────────────────────────────────────
     Initialisation
     ────────────────────────────────────────── */

  async init() {
    if (this.initialized) return

    this.ctx = new (window.AudioContext || window.webkitAudioContext)()

    // --- Master Gain ---
    this.masterGain = this.ctx.createGain()
    this.masterGain.gain.value = this.config.masterVolume

    // --- Compressor ---
    this.compressor = this.ctx.createDynamicsCompressor()
    this.compressor.threshold.value = -24
    this.compressor.knee.value = 12
    this.compressor.ratio.value = 4
    this.compressor.attack.value = 0.003
    this.compressor.release.value = 0.25

    // --- Limiter (compresseur avec ratio extrême) ---
    this.limiter = this.ctx.createDynamicsCompressor()
    this.limiter.threshold.value = -3
    this.limiter.knee.value = 0
    this.limiter.ratio.value = 20
    this.limiter.attack.value = 0.001
    this.limiter.release.value = 0.1

    // --- Layer Gains ---
    this.layer1Gain = this.ctx.createGain()
    this.layer1Gain.gain.value = this.config.layer1Volume

    this.layer2Gain = this.ctx.createGain()
    this.layer2Gain.gain.value = 0 // silencieux par défaut

    this.layer3Gain = this.ctx.createGain()
    this.layer3Gain.gain.value = this.config.layer3Volume

    this.layer4Gain = this.ctx.createGain()
    this.layer4Gain.gain.value = this.config.layer4Volume

    // --- Connexion du graphe ---
    this.layer1Gain.connect(this.masterGain)
    this.layer2Gain.connect(this.masterGain)
    this.layer3Gain.connect(this.masterGain)
    this.layer4Gain.connect(this.masterGain)
    this.masterGain.connect(this.compressor)
    this.compressor.connect(this.limiter)
    this.limiter.connect(this.ctx.destination)

    // --- Duck Manager ---
    this.duckManager = new DuckManager(
      this.ctx,
      this.layer1Gain,
      () => this.config.layer1Volume
    )

    // Pré-charger tous les sons
    await this._preloadSounds()

    this.initialized = true
  }

  /* ──────────────────────────────────────────
     Pré-chargement des buffers (fallback silencieux)
     ────────────────────────────────────────── */

  async _preloadSounds() {
    const entries = Object.entries(SOUND_FILES)
    let loaded = 0
    let skipped = 0

    await Promise.all(
      entries.map(async ([key, url]) => {
        try {
          const response = await fetch(url)
          if (!response.ok) {
            console.warn(`🔊 Son "${key}" indisponible (${response.status}), ignoré`)
            skipped++
            return
          }
          const arrayBuffer = await response.arrayBuffer()
          this.buffers[key] = await this.ctx.decodeAudioData(arrayBuffer)
          loaded++
        } catch (err) {
          console.warn(`🔊 Son "${key}" non chargé, ignoré :`, err.message)
          skipped++
        }
      })
    )

  }

  /* ──────────────────────────────────────────
     Enable / Disable
     ────────────────────────────────────────── */

  async enable() {
    if (!this.initialized) await this.init()

    // Attempt to resume AudioContext (may silently stay suspended due to autoplay policy)
    if (this.ctx.state === 'suspended') {
      await this.ctx.resume().catch(() => {})
    }

    this.enabled = true

    if (this.ctx.state === 'running') {
      if (this.matchActive) {
        this._startLayer1()
        this._startLayer4()
      }
    } else {
      console.warn(
        '🔊 Audio activé mais AudioContext suspended — en attente d\'un geste utilisateur'
      )
      this._waitForUserGesture()
    }

    // React to future state changes (e.g. parent page resumes our context)
    if (!this._stateChangeBound) {
      this._stateChangeBound = true
      this.ctx.addEventListener('statechange', () => {
        if (this.ctx.state === 'running' && this.enabled) {
          if (this.matchActive && !this.layer1.isPlaying) {
            this._startLayer1()
            this._startLayer4()
          }
        }
      })
    }
  }

  /**
   * Set up click/touchstart handlers so that the first user gesture
   * resumes the AudioContext and starts audio playback.
   */
  _waitForUserGesture() {
    if (this._gestureHandlerSet) return
    this._gestureHandlerSet = true

    const resume = async () => {
      if (!this.ctx || this.ctx.state === 'running') return
      await this.ctx.resume().catch(() => {})
      if (this.ctx.state === 'running') {
        document.removeEventListener('click', resume, true)
        document.removeEventListener('touchstart', resume, true)
        this._gestureHandlerSet = false
        if (this.enabled && this.matchActive && !this.layer1.isPlaying) {
          this._startLayer1()
          this._startLayer4()
        }
      }
    }

    document.addEventListener('click', resume, true)
    document.addEventListener('touchstart', resume, true)
  }

  disable() {
    this.enabled = false
    this._stopLayer1()
    this._stopLayer4()
    this._transitionLayer2('idle')
    this._stopAllLayer3()
    if (this.duckManager) this.duckManager.releaseAll()
  }

  /* ──────────────────────────────────────────
     API publique — appelée par le radar
     ────────────────────────────────────────── */

  /**
   * Appelé par ball-controller.js après chaque événement affiché.
   * @param {string} eventType — type d'événement du radar
   * @param {string} [team] — 'home', 'away' ou 'neutral'
   */
  onEvent(eventType, team) {
    if (!this.enabled) return

    this.lastTeam = team || null

    // Demander au resolver quoi faire
    const decision = this.resolver.resolve(eventType)

    switch (decision.action) {
      case 'transition_layer2':
        this._transitionLayer2(decision.layer2State)
        break

      case 'play_stack':
        this._playSoundStack(decision.stack)
        if (decision.layer2State) this._transitionLayer2(decision.layer2State)
        break

      case 'play_oneshot':
        this._playOneShot(decision.soundKey)
        if (decision.layer2State) this._transitionLayer2(decision.layer2State)
        break

      case 'ignore':
        // Rien à faire — le resolver a ses raisons
        break
    }

    // Si l'événement n'est pas un état Layer 2 et que Layer 2 est dans un
    // état de basse priorité (corner, dangerousAttack), revenir à idle
    // Sauf si la décision inclut déjà une transition Layer 2 (ex: coup d'envoi)
    if (decision.action !== 'transition_layer2' && decision.action !== 'ignore' && !decision.layer2State) {
      const currentL2 = LAYER2_STATES[this.layer2.currentState]
      if (currentL2 && currentL2.priority <= 2 && this.layer2.currentState !== 'idle') {
        // Vérifier que l'événement n'est pas un événement de flux passif
        const passiveEvents = ['Possession', 'Attaque', 'Touche', 'Ballons perdus']
        if (!passiveEvents.includes(eventType)) {
          this._transitionLayer2('idle')
        }
      }
    }

    // Layer 4 — réactions aux événements
    this._onLayer4Event(eventType)

    // Coups d'envoi relancent les chants si désactivés
    const kickoffEvents = ["Coup d'envoi", "Coup d'envoi 2ème mi-temps",
      "Coup d'envoi prolongation", "Coup d'envoi 2ème MT prolongation"]
    if (kickoffEvents.includes(eventType) && !this.layer4.active) {
      this._startLayer4()
    }
  }

  /**
   * Appelé quand la connexion MQTT est établie.
   */
  onMatchStart() {
    this.matchActive = true
    this.resolver.reset()
    if (this.enabled) {
      this._startLayer1()
      this._startLayer4()
    }
  }

  /**
   * Appelé quand la connexion MQTT est coupée.
   */
  onMatchEnd() {
    this.matchActive = false
    this._stopLayer1()
    this._stopLayer4()
    this._transitionLayer2('idle')
    this._stopAllLayer3()
    if (this.duckManager) this.duckManager.releaseAll()
    this.resolver.reset()
  }

  /* ──────────────────────────────────────────
     Layer 1 — Ambiance de fond (crossfade loop)
     ────────────────────────────────────────── */

  _startLayer1() {
    if (!this.buffers.ambiance || this.layer1.isPlaying) return
    this.layer1.isPlaying = true
    this._playCrossfadeInstance('ambiance', this.layer1, this.layer1Gain)
  }

  _stopLayer1() {
    if (!this.layer1.isPlaying) return
    this.layer1.isPlaying = false
    this._stopCrossfadeLoop(this.layer1, 1.0)
  }

  /* ──────────────────────────────────────────
     Layer 2 — Machine d'états
     ────────────────────────────────────────── */

  /**
   * Transitionner vers un nouvel état Layer 2.
   * Compare les priorités et applique fadeIn/fadeOut appropriés.
   * @param {string} newStateName — nom de l'état cible (clé de LAYER2_STATES)
   */
  _transitionLayer2(newStateName) {
    const currentStateName = this.layer2.currentState
    if (newStateName === currentStateName) return

    const currentState = LAYER2_STATES[currentStateName]
    const nextState = LAYER2_STATES[newStateName]
    if (!nextState) return

    // Fade-out avec la durée de l'état SORTANT
    if (currentStateName !== 'idle' && currentState) {
      const fadeOutDuration = currentState.fadeOut
      const now = this.ctx.currentTime
      this.layer2Gain.gain.cancelScheduledValues(now)
      this.layer2Gain.gain.setValueAtTime(this.layer2Gain.gain.value, now)
      this.layer2Gain.gain.linearRampToValueAtTime(0, now + fadeOutDuration)

      // Arrêter les sources après le fade-out
      const layerRef = this.layer2
      setTimeout(() => {
        if (layerRef.currentState === newStateName || newStateName === 'idle') {
          this._stopCrossfadeLoop(layerRef, 0)
          layerRef.isPlaying = false
        }
      }, fadeOutDuration * 1000 + 100)
    }

    // Mettre à jour l'état
    this.layer2.currentState = newStateName

    // Fade-in avec la durée de l'état ENTRANT
    if (nextState.key && this.buffers[nextState.key]) {
      const fadeInDuration = nextState.fadeIn

      // Petit délai pour laisser le fade-out commencer
      setTimeout(() => {
        if (this.layer2.currentState !== newStateName) return // état changé entre-temps

        this.layer2.isPlaying = true
        this._playCrossfadeInstance(nextState.key, this.layer2, this.layer2Gain)

        const now = this.ctx.currentTime
        this.layer2Gain.gain.cancelScheduledValues(now)
        this.layer2Gain.gain.setValueAtTime(0, now)
        this.layer2Gain.gain.linearRampToValueAtTime(
          this.config.layer2Volume,
          now + fadeInDuration
        )
      }, currentStateName !== 'idle' ? (currentState.fadeOut * 1000 * 0.3) : 0)

    } else if (newStateName === 'idle') {
    }
  }

  /* ──────────────────────────────────────────
     Layer 3 — Événements ponctuels (one-shots)
     ────────────────────────────────────────── */

  _playOneShot(bufferKey, volumeMultiplier = 1.0, duckLevel = null) {
    // Fallback : si le buffer demandé n'existe pas, tenter un fallback
    if (!this.buffers[bufferKey]) {
      const fallback = SOUND_FALLBACKS[bufferKey]
      if (fallback && this.buffers[fallback]) {
        bufferKey = fallback
      } else {
        return null
      }
    }

    const sourceId = `oneshot_${++_sourceIdCounter}`
    const source = this.ctx.createBufferSource()
    source.buffer = this.buffers[bufferKey]

    const sourceGain = this.ctx.createGain()
    const now = this.ctx.currentTime
    const duration = source.buffer.duration

    // Fade-in
    sourceGain.gain.setValueAtTime(0, now)
    sourceGain.gain.linearRampToValueAtTime(volumeMultiplier, now + this.config.layer3FadeIn)

    // Fade-out avant la fin
    const fadeOutStart = Math.max(0, duration - this.config.layer3FadeOut)
    sourceGain.gain.setValueAtTime(volumeMultiplier, now + fadeOutStart)
    sourceGain.gain.linearRampToValueAtTime(0, now + duration)

    source.connect(sourceGain)
    sourceGain.connect(this.layer3Gain)

    this.layer3.activeSources.add(source)

    // Duck Layer 1 si demandé
    if (duckLevel !== null && this.duckManager) {
      this.duckManager.requestDuck(sourceId, duckLevel)
    }

    source.onended = () => {
      this.layer3.activeSources.delete(source)
      if (duckLevel !== null && this.duckManager) {
        this.duckManager.releaseDuck(sourceId)
      }
      try { sourceGain.disconnect() } catch { /* ignore */ }
    }

    source.start(0)
    return sourceId
  }

  /**
   * Jouer une séquence de sons (sound stack).
   * Chaque son du stack peut avoir un délai et un duck indépendant.
   * @param {Array} stack — tableau de { key, delay, volume, duck }
   */
  _playSoundStack(stack) {
    if (!stack || stack.length === 0) return

    for (const entry of stack) {
      const { key, delay = 0, volume = 1.0, duck = null } = entry

      if (delay === 0) {
        this._playOneShot(key, volume, duck)
      } else {
        setTimeout(() => {
          this._playOneShot(key, volume, duck)
        }, delay * 1000)
      }
    }
  }

  _stopAllLayer3() {
    for (const source of this.layer3.activeSources) {
      try { source.stop() } catch { /* ignore */ }
    }
    this.layer3.activeSources.clear()
  }

  /* ──────────────────────────────────────────
     Layer 4 — Chants de fond (timer aléatoire)
     ────────────────────────────────────────── */

  /**
   * Démarrer le système de chants aléatoires.
   * Un timer planifie le prochain chant à intervalle aléatoire.
   */
  _startLayer4() {
    // Vérifier qu'au moins un chant est chargé
    const available = LAYER4_CONFIG.chantKeys.filter(k => this.buffers[k])
    if (available.length === 0) {
      console.warn('🔊 Layer 4 : aucun chant chargé, désactivé')
      return
    }

    this.layer4.active = true
    this._scheduleNextChant()
  }

  /**
   * Arrêter le système de chants.
   */
  _stopLayer4() {
    this.layer4.active = false
    if (this.layer4.timer) {
      clearTimeout(this.layer4.timer)
      this.layer4.timer = null
    }
    this._fadeOutChant(0.5)
  }

  /**
   * Planifier le prochain chant après un délai aléatoire.
   */
  _scheduleNextChant() {
    if (!this.layer4.active) return

    const [minDelay, maxDelay] = LAYER4_CONFIG.interval
    const delayMs = (minDelay + Math.random() * (maxDelay - minDelay)) * 1000

    this.layer4.timer = setTimeout(() => {
      this.layer4.timer = null
      if (!this.layer4.active || !this.enabled || !this.matchActive) return

      // Vérifier si l'état Layer 2 bloque les chants
      if (LAYER4_CONFIG.blockedByStates.includes(this.layer2.currentState)) {
        // Ré-essayer dans 15s
        this._scheduleNextChant()
        return
      }

      this._playChant()
    }, delayMs)
  }

  /**
   * Jouer un chant aléatoire (one-shot avec fade-in/fade-out).
   */
  _playChant() {
    const available = LAYER4_CONFIG.chantKeys.filter(k => this.buffers[k])
    if (available.length === 0) return

    // Choisir un chant différent du dernier
    let key
    if (available.length === 1) {
      key = available[0]
    } else {
      const candidates = available.filter(k => k !== this.layer4.lastChantKey)
      key = candidates[Math.floor(Math.random() * candidates.length)]
    }

    this.layer4.lastChantKey = key

    const source = this.ctx.createBufferSource()
    source.buffer = this.buffers[key]

    const sourceGain = this.ctx.createGain()
    const now = this.ctx.currentTime
    const duration = source.buffer.duration
    const { fadeIn, fadeOut } = LAYER4_CONFIG

    // Fade-in
    sourceGain.gain.setValueAtTime(0, now)
    sourceGain.gain.linearRampToValueAtTime(1, now + fadeIn)

    // Fade-out avant la fin
    const fadeOutStart = Math.max(fadeIn, duration - fadeOut)
    sourceGain.gain.setValueAtTime(1, now + fadeOutStart)
    sourceGain.gain.linearRampToValueAtTime(0, now + duration)

    source.connect(sourceGain)
    sourceGain.connect(this.layer4Gain)

    this.layer4.currentSource = source
    this.layer4.currentSourceGain = sourceGain
    this.layer4.isPlaying = true

    source.onended = () => {
      this.layer4.isPlaying = false
      this.layer4.currentSource = null
      this.layer4.currentSourceGain = null
      try { sourceGain.disconnect() } catch { /* ignore */ }

      // Planifier le prochain chant
      if (this.layer4.active) {
        this._scheduleNextChant()
      }
    }

    source.start(0)
  }

  /**
   * Couper le chant en cours avec un fade-out.
   * @param {number} fadeDuration — durée du fade-out en secondes
   */
  _fadeOutChant(fadeDuration = 1.0) {
    if (!this.layer4.isPlaying || !this.layer4.currentSourceGain) return

    const now = this.ctx.currentTime
    const gain = this.layer4.currentSourceGain
    const source = this.layer4.currentSource

    gain.gain.cancelScheduledValues(now)
    gain.gain.setValueAtTime(gain.gain.value, now)
    gain.gain.linearRampToValueAtTime(0, now + fadeDuration)

    // Stopper après le fade
    setTimeout(() => {
      try { source.stop() } catch { /* ignore */ }
    }, fadeDuration * 1000 + 100)

    this.layer4.isPlaying = false
    this.layer4.currentSource = null
    this.layer4.currentSourceGain = null
  }

  /**
   * Appelé par onEvent pour gérer les interactions Layer 4.
   * @param {string} eventType — type d'événement
   */
  _onLayer4Event(eventType) {
    if (!this.layer4.active) return

    // Événements qui tuent le chant immédiatement
    if (LAYER4_CONFIG.killEvents.includes(eventType)) {
      this._fadeOutChant(0.5)
      // Annuler le timer — les chants reprennent au prochain coup d'envoi
      if (this.layer4.timer) {
        clearTimeout(this.layer4.timer)
        this.layer4.timer = null
      }
      this.layer4.active = false
      return
    }

    // États Layer 2 bloquants → fade-out du chant en cours
    if (LAYER4_CONFIG.blockedByStates.includes(this.layer2.currentState) && this.layer4.isPlaying) {
      this._fadeOutChant(1.5)
    }
  }

  /* ──────────────────────────────────────────
     Crossfade Loop (utilisé par Layer 1 et 2)
     ──────────────────────────────────────────
     Technique du double-buffer :
     Quand le son approche de sa fin, on démarre
     une 2ème instance avec fade-in pendant que
     la 1ère fait un fade-out. Les deux se
     chevauchent pendant crossfadeDuration. */

  _playCrossfadeInstance(bufferKey, layerState, layerGainNode) {
    if (!layerState.isPlaying) return

    const buffer = this.buffers[bufferKey]
    if (!buffer) return

    const source = this.ctx.createBufferSource()
    source.buffer = buffer

    const sourceGain = this.ctx.createGain()
    source.connect(sourceGain)
    sourceGain.connect(layerGainNode)

    // Fade-in de cette instance
    const now = this.ctx.currentTime
    sourceGain.gain.setValueAtTime(0, now)
    sourceGain.gain.linearRampToValueAtTime(
      1,
      now + this.config.crossfadeDuration
    )

    // Sauvegarder les références (pour pouvoir stopper proprement)
    layerState.currentSource = source
    layerState.currentSourceGain = sourceGain

    source.start(0)

    // Programmer le crossfade avant la fin du buffer
    const durationMs = buffer.duration * 1000
    const preloadMs = this.config.crossfadePreload * 1000
    const scheduleMs = Math.max(100, durationMs - preloadMs)

    layerState.crossfadeTimer = setTimeout(() => {
      if (!layerState.isPlaying) return

      // Démarrer la prochaine instance (va fade-in)
      this._playCrossfadeInstance(bufferKey, layerState, layerGainNode)

      // Fade-out de l'instance courante
      const fadeNow = this.ctx.currentTime
      sourceGain.gain.cancelScheduledValues(fadeNow)
      sourceGain.gain.setValueAtTime(sourceGain.gain.value, fadeNow)
      sourceGain.gain.linearRampToValueAtTime(
        0,
        fadeNow + this.config.crossfadeDuration
      )

      // Nettoyage après le fade-out
      setTimeout(
        () => {
          try { source.stop() } catch { /* ignore */ }
          try { sourceGain.disconnect() } catch { /* ignore */ }
        },
        this.config.crossfadeDuration * 1000 + 200
      )
    }, scheduleMs)

    // Sécurité : nettoyage si le son se termine naturellement
    source.onended = () => {
      try { sourceGain.disconnect() } catch { /* ignore */ }
    }
  }

  /**
   * Stopper un crossfade loop proprement.
   * @param {object} layerState — état de la couche
   * @param {number} fadeOut — durée du fade-out final (secondes)
   */
  _stopCrossfadeLoop(layerState, fadeOut = 0.5) {
    // Annuler le prochain crossfade programmé
    if (layerState.crossfadeTimer) {
      clearTimeout(layerState.crossfadeTimer)
      layerState.crossfadeTimer = null
    }

    // Fade-out et nettoyage de la source en cours
    if (layerState.currentSourceGain && this.ctx) {
      const now = this.ctx.currentTime
      const gain = layerState.currentSourceGain
      const source = layerState.currentSource

      if (fadeOut > 0) {
        gain.gain.cancelScheduledValues(now)
        gain.gain.setValueAtTime(gain.gain.value, now)
        gain.gain.linearRampToValueAtTime(0, now + fadeOut)

        setTimeout(
          () => {
            try { source.stop() } catch { /* ignore */ }
            try { gain.disconnect() } catch { /* ignore */ }
          },
          fadeOut * 1000 + 100
        )
      } else {
        try { source.stop() } catch { /* ignore */ }
        try { gain.disconnect() } catch { /* ignore */ }
      }
    }

    layerState.currentSource = null
    layerState.currentSourceGain = null
  }

  /* ──────────────────────────────────────────
     Volume & configuration
     ────────────────────────────────────────── */

  setMasterVolume(value) {
    this.config.masterVolume = value
    if (this.masterGain && this.ctx) {
      this.masterGain.gain.linearRampToValueAtTime(
        value,
        this.ctx.currentTime + 0.1
      )
    }
  }

  setConfig(newConfig) {
    Object.assign(this.config, newConfig)

    if (!this.ctx) return
    const now = this.ctx.currentTime

    if (newConfig.masterVolume !== undefined && this.masterGain) {
      this.masterGain.gain.linearRampToValueAtTime(
        newConfig.masterVolume,
        now + 0.1
      )
    }
    if (newConfig.layer1Volume !== undefined && this.layer1Gain) {
      this.layer1Gain.gain.linearRampToValueAtTime(
        newConfig.layer1Volume,
        now + 0.1
      )
    }
    if (
      newConfig.layer2Volume !== undefined &&
      this.layer2.currentState !== 'idle' &&
      this.layer2Gain
    ) {
      this.layer2Gain.gain.linearRampToValueAtTime(
        newConfig.layer2Volume,
        now + 0.1
      )
    }
    if (newConfig.layer3Volume !== undefined && this.layer3Gain) {
      this.layer3Gain.gain.linearRampToValueAtTime(
        newConfig.layer3Volume,
        now + 0.1
      )
    }
  }

  /* ──────────────────────────────────────────
     API rétro-compatible pour playground-audio.html
     ────────────────────────────────────────── */

  /** @deprecated Utiliser _transitionLayer2('dangerousAttack') */
  _activateLayer2() {
    this._transitionLayer2('dangerousAttack')
  }

  /** @deprecated Utiliser _transitionLayer2('idle') */
  _deactivateLayer2() {
    this._transitionLayer2('idle')
  }
}

// Singleton — accessible globalement via window.stadiumAudio
const engine = new StadiumAudioEngine()
export default engine
